// Segments (SPEC §4 Segments.tsx) — CRUD on the segments table with a
// filter-builder that mirrors the SegmentDef jsonb shape, a live preview via
// the segment_client_ids RPC (the single segment evaluator), a "Text this
// segment" action that goes through the worker's /sms/bulk endpoint, and
// delete. Member counts on the list also come from segment_client_ids so the
// UI can never disagree with what a bulk send or campaign would target.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { sendBulk } from "../lib/api";
import { fmtDate } from "../lib/dates";
import { money } from "../lib/format";
import Modal from "../components/Modal";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import { SEGMENT_LABELS } from "../components/Badges";
import type { Id, Segment, SegmentDef } from "../lib/types";

/* ---------------------------------------------------------------- def <-> form */

type TriState = "" | "yes" | "no";

interface DefForm {
  has_active_package: TriState;
  one_time_only: TriState;
  min_ltv: string;
  max_ltv: string;
  last_visit_gt_weeks: string;
  last_visit_lt_weeks: string;
  min_visits: string;
  max_visits: string;
  tags: string; // comma-separated
  acquisition_source: string; // "" = any
  rfm_segment: string; // "" = any
  is_whale: TriState; // "" = key omitted (anyone) · yes = whales only · no = non-whales only
  min_avg_ticket: string;
}

const EMPTY_FORM: DefForm = {
  has_active_package: "",
  one_time_only: "",
  min_ltv: "",
  max_ltv: "",
  last_visit_gt_weeks: "",
  last_visit_lt_weeks: "",
  min_visits: "",
  max_visits: "",
  tags: "",
  acquisition_source: "",
  rfm_segment: "",
  is_whale: "",
  min_avg_ticket: "",
};

function numOrUndef(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Build the SegmentDef jsonb from the form — only explicitly-set keys. */
function formToDef(f: DefForm): SegmentDef {
  const def: SegmentDef = {};
  if (f.has_active_package) def.has_active_package = f.has_active_package === "yes";
  // "Exactly one visit" uses the evaluator's one_time_only flag. "More than one
  // visit" maps to min_visits >= 2 below — {one_time_only:false} would match
  // every client, because the evaluator only checks one_time_only when true.
  if (f.one_time_only === "yes") def.one_time_only = true;
  const min_ltv = numOrUndef(f.min_ltv);
  if (min_ltv !== undefined) def.min_ltv = min_ltv;
  const max_ltv = numOrUndef(f.max_ltv);
  if (max_ltv !== undefined) def.max_ltv = max_ltv;
  const gtw = numOrUndef(f.last_visit_gt_weeks);
  if (gtw !== undefined) def.last_visit_gt_weeks = gtw;
  const ltw = numOrUndef(f.last_visit_lt_weeks);
  if (ltw !== undefined) def.last_visit_lt_weeks = ltw;
  const minv = numOrUndef(f.min_visits);
  if (minv !== undefined) def.min_visits = minv;
  if (f.one_time_only === "no") def.min_visits = Math.max(def.min_visits ?? 2, 2);
  const maxv = numOrUndef(f.max_visits);
  if (maxv !== undefined) def.max_visits = maxv;
  const tags = f.tags
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tags.length > 0) def.tags = tags;
  if (f.acquisition_source) def.acquisition_source = f.acquisition_source;
  if (f.rfm_segment) def.rfm_segment = f.rfm_segment;
  // Unlike one_time_only, the evaluator compares is_whale by EQUALITY
  // (v.is_whale = def.is_whale), so false meaningfully selects non-whales.
  if (f.is_whale) def.is_whale = f.is_whale === "yes";
  const minTicket = numOrUndef(f.min_avg_ticket);
  if (minTicket !== undefined) def.min_avg_ticket = minTicket;
  return def;
}

function defToForm(def: SegmentDef): DefForm {
  return {
    has_active_package:
      def.has_active_package === undefined ? "" : def.has_active_package ? "yes" : "no",
    one_time_only: def.one_time_only === undefined ? "" : def.one_time_only ? "yes" : "no",
    min_ltv: def.min_ltv?.toString() ?? "",
    max_ltv: def.max_ltv?.toString() ?? "",
    last_visit_gt_weeks: def.last_visit_gt_weeks?.toString() ?? "",
    last_visit_lt_weeks: def.last_visit_lt_weeks?.toString() ?? "",
    min_visits: def.min_visits?.toString() ?? "",
    max_visits: def.max_visits?.toString() ?? "",
    tags: (def.tags ?? []).join(", "),
    acquisition_source: def.acquisition_source ?? "",
    rfm_segment: def.rfm_segment ?? "",
    is_whale: def.is_whale === undefined ? "" : def.is_whale ? "yes" : "no",
    min_avg_ticket: def.min_avg_ticket?.toString() ?? "",
  };
}

const SOURCE_LABELS: Record<string, string> = {
  google_ads: "Google Ads",
  seo_gbp: "SEO (GBP)",
  referral: "Referral",
  organic: "Organic",
  other: "Other",
};

/** Human summary of a definition for the list ("everyone" when empty). */
function describeDef(def: SegmentDef): string {
  const parts: string[] = [];
  if (def.one_time_only !== undefined)
    parts.push(def.one_time_only ? "one-time only" : "not one-time");
  if (def.has_active_package !== undefined)
    parts.push(def.has_active_package ? "active package" : "no active package");
  if (def.min_ltv !== undefined) parts.push(`LTV ≥ ${money(def.min_ltv)}`);
  if (def.max_ltv !== undefined) parts.push(`LTV ≤ ${money(def.max_ltv)}`);
  if (def.last_visit_gt_weeks !== undefined)
    parts.push(`last visit > ${def.last_visit_gt_weeks}w ago`);
  if (def.last_visit_lt_weeks !== undefined)
    parts.push(`last visit < ${def.last_visit_lt_weeks}w ago`);
  if (def.min_visits !== undefined) parts.push(`visits ≥ ${def.min_visits}`);
  if (def.max_visits !== undefined) parts.push(`visits ≤ ${def.max_visits}`);
  if (def.tags && def.tags.length > 0) parts.push(`tags: ${def.tags.join(", ")}`);
  if (def.acquisition_source)
    parts.push(`source: ${SOURCE_LABELS[def.acquisition_source] ?? def.acquisition_source}`);
  if (def.rfm_segment)
    parts.push(`segment: ${SEGMENT_LABELS[def.rfm_segment] ?? def.rfm_segment}`);
  if (def.is_whale !== undefined) parts.push(def.is_whale ? "whales only" : "non-whales only");
  if (def.min_avg_ticket !== undefined) parts.push(`avg ticket ≥ ${money(def.min_avg_ticket)}`);
  return parts.length > 0 ? parts.join(" · ") : "everyone";
}

async function evalSegment(def: SegmentDef): Promise<Id[]> {
  const { data, error } = await supabase.rpc("segment_client_ids", { p_def: def });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as { client_id: Id }[];
  return rows.map((r) => r.client_id);
}

/* ------------------------------------------------------------------- editor */

interface EditorProps {
  initial: Segment | null;
  clientNames: Map<string, string>;
  onClose: () => void;
  onSaved: () => void;
}

function SegmentEditor({ initial, clientNames, onClose, onSaved }: EditorProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [form, setForm] = useState<DefForm>(initial ? defToForm(initial.definition) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ ids: Id[]; loading: boolean; error: string | null }>({
    ids: [],
    loading: true,
    error: null,
  });

  const def = useMemo(() => formToDef(form), [form]);
  const defKey = JSON.stringify(def);

  // Debounced live preview via the segment_client_ids RPC.
  useEffect(() => {
    let cancelled = false;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(() => {
      evalSegment(JSON.parse(defKey) as SegmentDef).then(
        (ids) => {
          if (!cancelled) setPreview({ ids, loading: false, error: null });
        },
        (err: unknown) => {
          if (!cancelled)
            setPreview({
              ids: [],
              loading: false,
              error: err instanceof Error ? err.message : String(err),
            });
        },
      );
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [defKey]);

  function set<K extends keyof DefForm>(key: K, value: DefForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give the segment a name.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (initial) {
        const { error: err } = await supabase
          .from("segments")
          .update({ name: trimmed, definition: def })
          .eq("id", initial.id);
        if (err) throw new Error(err.message);
      } else {
        const { error: err } = await supabase
          .from("segments")
          .insert({ name: trimmed, definition: def });
        if (err) throw new Error(err.message);
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(/duplicate|unique/i.test(msg) ? `A segment named "${trimmed}" already exists.` : msg);
      setSaving(false);
    }
  }

  const previewNames = preview.ids
    .slice(0, 20)
    .map((id) => clientNames.get(String(id)) ?? `Client ${String(id)}`);

  return (
    <Modal
      open
      wide
      title={initial ? `Edit segment: ${initial.name}` : "New segment"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : "Save segment"}
          </button>
        </>
      }
    >
      {error && <div className="error-banner">{error}</div>}

      <div className="field">
        <label htmlFor="seg-name">Name</label>
        <input
          id="seg-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Overdue for rebook (6w)"
          disabled={saving}
        />
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="seg-pkg">Active package</label>
          <select
            id="seg-pkg"
            value={form.has_active_package}
            onChange={(e) => set("has_active_package", e.target.value as TriState)}
          >
            <option value="">Any</option>
            <option value="yes">Has active package</option>
            <option value="no">No active package</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="seg-once">One-time only</label>
          <select
            id="seg-once"
            value={form.one_time_only}
            onChange={(e) => set("one_time_only", e.target.value as TriState)}
          >
            <option value="">Any</option>
            <option value="yes">Exactly one visit</option>
            <option value="no">More than one visit</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="seg-source">Acquisition source</label>
          <select
            id="seg-source"
            value={form.acquisition_source}
            onChange={(e) => set("acquisition_source", e.target.value)}
          >
            <option value="">Any</option>
            <option value="google_ads">Google Ads</option>
            <option value="seo_gbp">SEO (GBP)</option>
            <option value="referral">Referral</option>
            <option value="organic">Organic</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="seg-min-ltv">Min LTV ($)</label>
          <input
            id="seg-min-ltv"
            type="number"
            min={0}
            value={form.min_ltv}
            onChange={(e) => set("min_ltv", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="seg-max-ltv">Max LTV ($)</label>
          <input
            id="seg-max-ltv"
            type="number"
            min={0}
            value={form.max_ltv}
            onChange={(e) => set("max_ltv", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="seg-min-visits">Min visits</label>
          <input
            id="seg-min-visits"
            type="number"
            min={0}
            step={1}
            value={form.min_visits}
            onChange={(e) => set("min_visits", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="seg-max-visits">Max visits</label>
          <input
            id="seg-max-visits"
            type="number"
            min={0}
            step={1}
            value={form.max_visits}
            onChange={(e) => set("max_visits", e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="seg-rfm">RFM segment</label>
          <select
            id="seg-rfm"
            value={form.rfm_segment}
            onChange={(e) => set("rfm_segment", e.target.value)}
          >
            <option value="">Any</option>
            {Object.entries(SEGMENT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="seg-min-ticket">Min avg ticket ($)</label>
          <input
            id="seg-min-ticket"
            type="number"
            min={0}
            value={form.min_avg_ticket}
            onChange={(e) => set("min_avg_ticket", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="seg-whale">Whales</label>
          <select
            id="seg-whale"
            value={form.is_whale}
            onChange={(e) => set("is_whale", e.target.value as TriState)}
          >
            <option value="">Anyone</option>
            <option value="yes">Whales only</option>
            <option value="no">Non-whales only</option>
          </select>
          <span className="field-hint">Whale = avg ticket ≥ $600 or LTV ≥ $2,000</span>
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="seg-gtw">Last visit more than (weeks ago)</label>
          <input
            id="seg-gtw"
            type="number"
            min={0}
            step={1}
            value={form.last_visit_gt_weeks}
            onChange={(e) => set("last_visit_gt_weeks", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="seg-ltw">Last visit less than (weeks ago)</label>
          <input
            id="seg-ltw"
            type="number"
            min={0}
            step={1}
            value={form.last_visit_lt_weeks}
            onChange={(e) => set("last_visit_lt_weeks", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="seg-tags">Tags (comma-separated — client has any of these tags)</label>
          <input
            id="seg-tags"
            value={form.tags}
            onChange={(e) => set("tags", e.target.value)}
            placeholder="vip, nad"
          />
        </div>
      </div>

      <div className="panel mt-1">
        <div className="panel-title">Live preview — {describeDef(def)}</div>
        {preview.error && <div className="error-banner">{preview.error}</div>}
        {preview.loading ? (
          <div className="loading">
            <span className="spinner" /> Evaluating…
          </div>
        ) : (
          <>
            <div className="bold mb-1">
              {preview.ids.length} client{preview.ids.length === 1 ? "" : "s"} match
            </div>
            {previewNames.length > 0 && (
              <div className="chips">
                {previewNames.map((n, i) => (
                  <span className="chip" key={`${n}-${i}`}>
                    {n}
                  </span>
                ))}
                {preview.ids.length > 20 && (
                  <span className="muted small">+{preview.ids.length - 20} more</span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------- text modal */

interface TextModalProps {
  segment: Segment;
  memberCount: number | null;
  onClose: () => void;
}

function TextSegmentModal({ segment, memberCount, onClose }: TextModalProps) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  // Chunk-loop progress while the bulk send runs ("Sending 80/220…").
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);

  async function send() {
    if (!body.trim()) {
      setError("Write the message first.");
      return;
    }
    setSending(true);
    setError(null);
    setProgress(null);
    try {
      const res = await sendBulk({ segment_id: segment.id, body }, (sentSoFar, totalPlanned) =>
        setProgress({ sent: sentSoFar, total: totalPlanned }),
      );
      const skipped = res.skipped?.length ?? 0;
      setResult(
        `Sent ${res.sent} message${res.sent === 1 ? "" : "s"}` +
          (skipped > 0 ? ` · skipped ${skipped} (opt-out / no phone)` : ""),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      title={`Text segment: ${segment.name}`}
      onClose={onClose}
      footer={
        result ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void send()}
              disabled={sending || !body.trim()}
            >
              {sending
                ? progress
                  ? `Sending ${progress.sent}/${progress.total}…`
                  : "Sending…"
                : `Send${memberCount != null ? ` to ~${memberCount}` : ""}`}
            </button>
          </>
        )
      }
    >
      {error && <div className="error-banner">{error}</div>}
      {result && <div className="success-banner">{result}</div>}
      {!result && (
        <>
          {sending && progress && (
            <div className="info-banner">
              <span className="spinner" /> Sending {progress.sent}/{progress.total}… keep this
              window open until it finishes.
            </div>
          )}
          <div className="info-banner">
            Goes to every match of “{describeDef(segment.definition)}”
            {memberCount != null ? ` — about ${memberCount} client${memberCount === 1 ? "" : "s"}` : ""}.
            Opted-out and phone-less clients are skipped automatically.
          </div>
          <div className="field">
            <label htmlFor="seg-sms-body">Message</label>
            <textarea
              id="seg-sms-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {first_name}, it's Fresh IV Drips…"
              disabled={sending}
            />
            <span className="field-hint">
              {"{first_name}"} is replaced per recipient · {body.length} characters · messages go
              out in batches of 40, paced ~1.3s apart (Project Blue rate limit)
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}

/* --------------------------------------------------------------------- page */

interface ClientNameRow {
  id: Id;
  full_name: string;
}

export default function Segments() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<Segment | null | "new">(null);
  const [texting, setTexting] = useState<Segment | null>(null);
  const [counts, setCounts] = useState<Record<string, number | undefined>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const segmentsQ = useQuery<Segment[]>(async () => {
    const { data, error } = await supabase.from("segments").select("*").order("name");
    if (error) throw new Error(error.message);
    return (data ?? []) as Segment[];
  }, []);

  const clientsQ = useQuery<ClientNameRow[]>(async () => {
    const { data, error } = await supabase.from("v_client_list").select("id,full_name");
    if (error) throw new Error(error.message);
    return (data ?? []) as ClientNameRow[];
  }, []);

  const clientNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clientsQ.data ?? []) m.set(String(c.id), c.full_name);
    return m;
  }, [clientsQ.data]);

  // Member counts per saved segment — sequential RPC calls, updating as they land.
  useEffect(() => {
    const segments = segmentsQ.data;
    if (!segments || segments.length === 0) return;
    let cancelled = false;
    void (async () => {
      for (const s of segments) {
        try {
          const ids = await evalSegment(s.definition);
          if (cancelled) return;
          setCounts((c) => ({ ...c, [s.id]: ids.length }));
        } catch {
          if (cancelled) return;
          setCounts((c) => ({ ...c, [s.id]: -1 }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [segmentsQ.data]);

  async function remove(segment: Segment) {
    if (
      !window.confirm(
        `Delete segment "${segment.name}"? Campaigns pointing at it will lose their segment and stop matching until repointed.`,
      )
    )
      return;
    setActionError(null);
    const { error } = await supabase.from("segments").delete().eq("id", segment.id);
    if (error) {
      setActionError(error.message);
      return;
    }
    setNotice(`Deleted "${segment.name}".`);
    segmentsQ.reload();
  }

  const segments = segmentsQ.data ?? [];
  const textingCount = texting ? counts[texting.id] : undefined;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Segments</h1>
          <div className="page-sub">
            Saved client filters — used for one-off texts and campaign blasts.
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setEditing("new")}>
            New segment
          </button>
        </div>
      </div>

      {notice && <div className="success-banner">{notice}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}
      {segmentsQ.error && <div className="error-banner">{segmentsQ.error}</div>}

      {segmentsQ.loading && !segmentsQ.data && (
        <div className="loading">
          <span className="spinner" /> Loading segments…
        </div>
      )}

      {segmentsQ.data && segments.length === 0 && (
        <div className="empty-state">No segments yet — create one to start targeting clients.</div>
      )}

      {segments.length > 0 && (
        <DataTable
          columns={
            [
              {
                key: "name",
                label: "Name",
                render: (s) => (
                  <Link
                    className="bold"
                    to={`/clients?segment=${s.id}`}
                    title="View members in Clients"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {s.name}
                  </Link>
                ),
              },
              {
                key: "definition",
                label: "Definition",
                render: (s) => <span className="muted">{describeDef(s.definition)}</span>,
                sortValue: (s) => describeDef(s.definition),
              },
              {
                key: "members",
                label: "Members",
                align: "right",
                width: "90px",
                sortValue: (s) => {
                  const count = counts[s.id];
                  return count !== undefined && count >= 0 ? count : null;
                },
                render: (s) => {
                  const count = counts[s.id];
                  if (count === undefined) return <span className="spinner" />;
                  if (count < 0)
                    return (
                      <span className="red" title="Preview failed — check the definition">
                        ?
                      </span>
                    );
                  return <>{count}</>;
                },
              },
              {
                key: "created_at",
                label: "Created",
                width: "110px",
                render: (s) => <span className="muted nowrap">{fmtDate(s.created_at)}</span>,
              },
              {
                key: "actions",
                label: "Actions",
                width: "400px",
                render: (s) => (
                  <span className="flex nowrap">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients?segment=${s.id}`);
                      }}
                    >
                      View members
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(s);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTexting(s);
                      }}
                    >
                      Text this segment
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        void remove(s);
                      }}
                    >
                      Delete
                    </button>
                  </span>
                ),
              },
            ] satisfies Column<Segment>[]
          }
          rows={segments}
          defaultSortKey="name"
          onRowClick={(s) => navigate(`/clients?segment=${s.id}`)}
        />
      )}

      {editing !== null && (
        <SegmentEditor
          initial={editing === "new" ? null : editing}
          clientNames={clientNames}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice("Segment saved.");
            setCounts({});
            segmentsQ.reload();
          }}
        />
      )}

      {texting && (
        <TextSegmentModal
          segment={texting}
          memberCount={textingCount !== undefined && textingCount >= 0 ? textingCount : null}
          onClose={() => setTexting(null)}
        />
      )}
    </div>
  );
}
